export const USER_DETAILS_KEY = "zTaskUserDetails";

const getUserDetails = () => {
  const storedUserDetailsString = localStorage.getItem(USER_DETAILS_KEY);
  if (!storedUserDetailsString) return null;
  try {
    return JSON.parse(storedUserDetailsString);
  } catch {
    return null;
  }
};

const roleName = () => {
  const details = getUserDetails();
  const role = details?.role;
  if (!role) return "";
  if (typeof role === "string") return role.toUpperCase().replace(/\s+/g, "_");
  return String(role?.name || role?.role_name || "")
    .toUpperCase()
    .replace(/\s+/g, "_");
};

/** Platform admins get full commercial UI in lab/local. */
export const isSuperAdmin = () => {
  const r = roleName();
  return r === "SUPER_ADMIN" || r === "ADMIN";
};

function permissionTitles() {
  const userDetails = getUserDetails();
  const perms = userDetails?.role?.permissions;
  if (!Array.isArray(perms)) return [];
  return perms
    .map((p) =>
      String(typeof p === "string" ? p : p?.title || p?.name || "")
        .trim()
        .toUpperCase()
    )
    .filter(Boolean);
}

function normalizeExpected(raw) {
  return String(raw || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");
}

/** Aliases so UI module keys match seeded titles (e.g. AR DEBIT NOTE ↔ DEBIT NOTE). */
function expandAliases(expected) {
  const e = normalizeExpected(expected);
  const aliases = new Set([e]);
  if (e.includes(" AR ")) {
    aliases.add(e.replace(" AR ", " "));
  }
  if (e.startsWith("VIEW DEBIT NOTE")) aliases.add("VIEW AR DEBIT NOTE");
  if (e.startsWith("VIEW CREDIT NOTE")) aliases.add("VIEW AR CREDIT NOTE");
  if (e.startsWith("VIEW RECEIPT")) aliases.add("VIEW AR RECEIPT");
  if (e.startsWith("CREATE DEBIT NOTE")) aliases.add("CREATE AR DEBIT NOTE");
  if (e.startsWith("CREATE CREDIT NOTE")) aliases.add("CREATE AR CREDIT NOTE");
  if (e.startsWith("CREATE RECEIPT")) aliases.add("CREATE AR RECEIPT");
  if (e.startsWith("APPROVE DEBIT NOTE") || e.startsWith("APPROVE AR DEBIT NOTE")) {
    aliases.add("APPROVE DEBIT NOTE");
    aliases.add("APPROVE AR DEBIT NOTE");
  }
  if (e.startsWith("APPROVE CREDIT NOTE") || e.startsWith("APPROVE AR CREDIT NOTE")) {
    aliases.add("APPROVE CREDIT NOTE");
    aliases.add("APPROVE AR CREDIT NOTE");
  }
  if (e.startsWith("APPROVE RECEIPT") || e.startsWith("APPROVE AR RECEIPT")) {
    aliases.add("APPROVE RECEIPT");
    aliases.add("APPROVE AR RECEIPT");
  }
  if (e.startsWith("VIEW AR DEBIT NOTE")) aliases.add("VIEW DEBIT NOTE");
  if (e.startsWith("VIEW AR CREDIT NOTE")) aliases.add("VIEW CREDIT NOTE");
  if (e.startsWith("VIEW AR RECEIPT")) aliases.add("VIEW RECEIPT");
  if (e === "VIEW WORK ORDER") aliases.add("VIEW ALL WORK ORDERS");
  if (e === "VIEW ALL WORK ORDERS") aliases.add("VIEW WORK ORDER");
  if (e === "PRINT INVOICE") {
    aliases.add("VIEW INVOICE");
    aliases.add("DOWNLOAD WORK ORDER PDF");
  }
  return aliases;
}

/**
 * Supports:
 *  - hasPermission("work-order", "view") → VIEW WORK ORDER
 *  - hasPermission("VIEW WORK ORDER")
 *  - hasPermission(["VIEW WORK ORDER", "VIEW ALL WORK ORDERS"])
 */
export const hasPermission = (moduleOrTitle, action) => {
  if (isSuperAdmin()) return true;

  const titles = permissionTitles();
  if (!titles.length) return false;

  let expectedList = [];
  if (Array.isArray(moduleOrTitle)) {
    expectedList = moduleOrTitle;
  } else if (action === undefined || action === null || action === "") {
    expectedList = [moduleOrTitle];
  } else {
    expectedList = [
      `${String(action).toUpperCase()} ${String(moduleOrTitle)
        .split("-")
        .join(" ")
        .toUpperCase()}`,
    ];
  }

  return expectedList.some((exp) => {
    const aliases = expandAliases(exp);
    return titles.some((t) => aliases.has(normalizeExpected(t)));
  });
};
